import type { ShiftPayAllowanceLine, ShiftPayBreakdown } from '../schads';
import { roundMoney, sumMoney } from '../money-rounding';
import {
  payrollBreakMinutesForEngine,
  type BreakSemantics,
  type BreakTreatmentSnapshotV1,
} from './break-treatment';
import { resolveAwardMinimumEngagementFloor } from './award-minimum-engagement';
import { DST_START_ORDINARY_TOP_UP_CODE } from './multi-zone-pay-clock';

// 1.3.0 (MZ-004, 2026-10-05): a minimum-engagement top-up on a shift that also
// carries the engine's DST-start ordinary top-up first makes up the hour lost
// to the clock change; the DST line keeps only the remainder (never double-paid).
// 1.4.0 (MZ-004 R3, 2026-10-06): when the worker took public holiday hours as
// leave (cl 29.2), the cl 22.9(j) / 25.1(d) rest-break 200% target is measured
// against the cash-election baseline (worked pay plus the premium taken as
// leave), so the leave hours and the top-up never pay the same hours twice.
// 1.4.1 (2026-10-08) — top-up amounts use exact half-up roundMoney.
export const AUTOMATIC_AWARD_TOPUP_VERSION = '1.4.1';

export type AutomaticAwardTopUpCode =
  | 'REST_BREAK_TOP_UP_ALLOWANCE'
  | 'SLEEPOVER_FLANKING_MINIMUM_TOP_UP_ALLOWANCE'
  | 'MINIMUM_ENGAGEMENT_TOP_UP_ALLOWANCE';

interface AutomaticAwardTopUpLine extends ShiftPayAllowanceLine {
  code: AutomaticAwardTopUpCode;
  units: number;
  unitType: 'hours';
  componentCode: string;
  xeroEarningsCategory: string;
  taxable: boolean;
  superable: boolean;
}

export interface AutomaticAwardTopUpInput {
  awardCode: string | null | undefined;
  employmentType: string | null | undefined;
  currentShiftStart?: Date | string | null;
  priorShiftEnd?: Date | string | null;
  priorShiftHadSleepover?: boolean;
  priorShiftHadOvertime?: boolean;
  schadsSleepoverFlankingTopUpHours?: number | null;
  hpssMinimumEngagementHours?: number | null;
  minimumEngagementMinutes?: number | null;
  minimumEngagementClauseRef?: string | null;
  minimumEngagementComponentCode?: string | null;
  /**
   * Classification stream (e.g. sacs / home_care_aged). Used to pick
   * SCHADS cl 10.5(a) 3h vs 10.5(b) 2h. Omitted stream uses 10.5(b).
   */
  classificationStream?: string | null;
  suppressMinimumEngagementTopUp?: boolean;
  /** SCHADS cl 25.10(f)(ii) — remote work is not topped up to the cl 10.5 floor. */
  isRemoteWork?: boolean;
  /**
   * S16 — work-period paid hours for minimum-engagement floor comparison.
   * When set (e.g. SAH multi-visit day on the anchor timesheet), the floor is
   * evaluated against this total rather than the single timesheet's
   * `breakdown.hoursWorked`, so the top-up is applied once per work period.
   *
   * PAYROLL MATH (not cosmetic): changing which hours denominator the shared
   * top-up formula uses changes emitted MINIMUM_ENGAGEMENT_TOP_UP_ALLOWANCE
   * dollars on production forecast/recompute/snapshot paths. Regression tests
   * must cover HPSS + profile callers.
   */
  minimumEngagementHoursWorkedOverride?: number | null;
}

export interface AutomaticAwardTopUpSummary {
  version: string;
  applied: Array<{
    code: AutomaticAwardTopUpCode;
    clauseRef: string;
    amount: number;
    units: number;
  }>;
}

export interface SleepoverFlankingTopUpShift {
  id: string;
  staffId: string;
  startTime: Date | string;
  endTime: Date | string;
  breakMinutes: number;
  breakTreatment?: BreakTreatmentSnapshotV1 | null;
  /** Omitted = LEGACY_28D1 (rebuild OFF). */
  breakSemantics?: BreakSemantics;
  hasSleepover: boolean;
}

type BreakdownWithTopUps = ShiftPayBreakdown & {
  automaticAwardTopUps?: AutomaticAwardTopUpSummary;
};

function round2(value: number): number {
  return roundMoney(value);
}

function positiveNumber(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
}

function parseTimeMs(value: Date | string | null | undefined): number | null {
  if (!value) return null;
  const ms = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function paidHoursForFlankingShift(shift: SleepoverFlankingTopUpShift): number {
  const startMs = parseTimeMs(shift.startTime);
  const endMs = parseTimeMs(shift.endTime);
  if (startMs === null || endMs === null || endMs <= startMs) return 0;
  const deductibleBreakMinutes = payrollBreakMinutesForEngine(
    shift.breakMinutes,
    shift.breakTreatment,
    shift.breakSemantics,
  );
  return Math.max(0, (endMs - startMs) / (60 * 60 * 1000) - deductibleBreakMinutes / 60);
}

export function precomputeSchadsSleepoverFlankingTopUps(
  shifts: SleepoverFlankingTopUpShift[],
): Map<string, number> {
  const result = new Map<string, number>();
  const byStaff = new Map<string, SleepoverFlankingTopUpShift[]>();
  for (const shift of shifts) {
    const staffRows = byStaff.get(shift.staffId) ?? [];
    staffRows.push(shift);
    byStaff.set(shift.staffId, staffRows);
  }

  for (const staffRows of byStaff.values()) {
    staffRows.sort((a, b) => {
      const aMs = parseTimeMs(a.startTime) ?? 0;
      const bMs = parseTimeMs(b.startTime) ?? 0;
      return aMs - bMs;
    });
    for (let index = 0; index < staffRows.length; index += 1) {
      const sleepover = staffRows[index];
      if (!sleepover?.hasSleepover) continue;
      const before = staffRows[index - 1] ?? null;
      const after = staffRows[index + 1] ?? null;
      if (!before || !after) continue;
      const beforeHours = paidHoursForFlankingShift(before);
      const afterHours = paidHoursForFlankingShift(after);
      if (beforeHours <= 0 || afterHours <= 0 || beforeHours >= 4 || afterHours >= 4) continue;

      const target = beforeHours >= afterHours ? before : after;
      const targetHours = target === before ? beforeHours : afterHours;
      const topUpHours = Math.max(0, 4 - targetHours);
      if (topUpHours <= 0) continue;
      result.set(target.id, Math.max(result.get(target.id) ?? 0, topUpHours));
    }
  }
  return result;
}

function restGapHours(input: AutomaticAwardTopUpInput): number | null {
  const priorEndMs = parseTimeMs(input.priorShiftEnd);
  const currentStartMs = parseTimeMs(input.currentShiftStart);
  if (priorEndMs === null || currentStartMs === null || currentStartMs < priorEndMs) return null;
  return (currentStartMs - priorEndMs) / (60 * 60 * 1000);
}

function workedPayForRestTarget(breakdown: ShiftPayBreakdown): number {
  // A cl 29.2 leave election withholds the 150% premium only because the same
  // value is credited as annual leave; the rest-break target counts it as paid.
  const election = (
    breakdown as ShiftPayBreakdown & { publicHolidayElection?: { cashPremiumNotPaid?: number } }
  ).publicHolidayElection;
  const premiumTakenAsLeave =
    typeof election?.cashPremiumNotPaid === 'number' && election.cashPremiumNotPaid > 0
      ? election.cashPremiumNotPaid
      : 0;
  return round2(
    breakdown.basePay +
      breakdown.penaltyPay +
      breakdown.shiftLoadingPay +
      breakdown.overtimePay +
      premiumTakenAsLeave,
  );
}

// Micro-duration guard rails for the top-up averaging rate. A glitch clock
// pair (observed live: 0.01h) can carry pay for a minimum billing increment;
// dividing that pay by the raw clocked hours back-derives an absurd hourly
// rate (31× base observed on Golden Bloom, 14-Aug-2026), which the
// minimum-engagement top-up then multiplies across ~2 hours. Average over at
// least the minimum payable quantum, and never exceed the highest legitimate
// penalty stack over base.
const MINIMUM_AVERAGING_HOURS = 0.25;
const MAXIMUM_PENALTY_STACK = 3.5;

function workedAverageRate(breakdown: ShiftPayBreakdown): number {
  if (breakdown.hoursWorked <= 0) return breakdown.baseHourlyRate;
  const averaged =
    workedPayForRestTarget(breakdown) / Math.max(breakdown.hoursWorked, MINIMUM_AVERAGING_HOURS);
  if (breakdown.baseHourlyRate > 0) {
    // eslint-disable-next-line statura/no-floating-point-money -- plausibility ceiling, not a payable amount; every consumer round2()s the resulting top-up before persistence
    return Math.min(averaged, breakdown.baseHourlyRate * MAXIMUM_PENALTY_STACK);
  }
  return averaged;
}

function ordinaryXeroCategoryForAward(awardCode: string): string {
  if (awardCode === 'MA000018') return 'AGED_CARE_ORDINARY';
  if (awardCode === 'MA000034') return 'NURSES_ORDINARY';
  return 'SCHADS_ORDINARY';
}

/**
 * MZ-004 D5 × minimum engagement. Reduces the engine's DST-start ordinary
 * top-up by the minimum-engagement hours (removing it when fully covered) and
 * returns the signed dollar change for the breakdown totals.
 */
function reconcileDstStartTopUpWithMinimumEngagement(
  lines: ShiftPayAllowanceLine[],
  minimumEngagementTopUpHours: number,
  baseHourlyRate: number,
): { lines: ShiftPayAllowanceLine[]; amountDelta: number } {
  const index = lines.findIndex((line) => line.code === DST_START_ORDINARY_TOP_UP_CODE);
  if (index < 0 || minimumEngagementTopUpHours <= 0) return { lines, amountDelta: 0 };
  const line = lines[index]!;
  const lostHours = positiveNumber(line.units);
  const remainingHours =
    Math.round(Math.max(0, lostHours - minimumEngagementTopUpHours) * 10_000) / 10_000;
  if (remainingHours >= lostHours) return { lines, amountDelta: 0 };
  // eslint-disable-next-line statura/no-floating-point-money -- same basis as the engine line (4-dp hours × ordinary rate), cent-rounded once.
  const remainingAmount = round2(remainingHours * baseHourlyRate);
  const next = [...lines];
  if (remainingHours <= 0) {
    next.splice(index, 1);
  } else {
    next[index] = {
      ...line,
      units: remainingHours,
      amount: remainingAmount,
      description: `Daylight saving start: ${Math.round(
        remainingHours * 60,
      )} minutes of rostered ordinary time lost to the clock change and not already paid by the minimum engagement, paid at the ordinary rate`,
    };
  }
  // eslint-disable-next-line statura/no-floating-point-money -- difference of two cent-rounded line amounts, re-normalised.
  return { lines: next, amountDelta: round2(remainingAmount - line.amount) };
}

function appendTopUp(
  lines: AutomaticAwardTopUpLine[],
  line: Omit<AutomaticAwardTopUpLine, 'taxable' | 'superable' | 'unitType'>,
): number {
  const amount = round2(line.amount);
  if (amount <= 0) return 0;
  lines.push({
    ...line,
    amount,
    unitType: 'hours',
    taxable: true,
    superable: true,
  });
  return amount;
}

/**
 * Apply automatic payroll top-ups for clauses whose triggering facts are
 * known at pay-run snapshot time. This deliberately leaves the base award
 * engines unchanged and records each adjustment as a normal allowance line so
 * the controlled RPC, component-line trigger and Xero mapper all receive an
 * auditable component.
 */
export function applyAutomaticAwardTopUps(
  breakdown: ShiftPayBreakdown,
  input: AutomaticAwardTopUpInput,
): ShiftPayBreakdown {
  const awardCode = (input.awardCode ?? '').toUpperCase();
  const employmentType = (input.employmentType ?? '').toUpperCase();
  const extraLines: AutomaticAwardTopUpLine[] = [];
  const extraWarnings: string[] = [];
  let extraAllowancePay = 0;
  // Engine lines after any MZ-004 D5 reconciliation with the minimum engagement.
  let engineAllowanceLines = breakdown.allowanceLines;

  if (awardCode === 'MA000018' && employmentType !== 'CASUAL' && breakdown.hoursWorked > 0) {
    const gapHours = restGapHours(input);
    const clauseRefs: string[] = [];
    if (gapHours !== null && gapHours < 8 && input.priorShiftHadSleepover) {
      clauseRefs.push('MA000018 cl 22.9(j)');
    }
    if (gapHours !== null && gapHours < 10 && input.priorShiftHadOvertime) {
      clauseRefs.push('MA000018 cl 25.1(d)');
    }
    if (clauseRefs.length > 0) {
      // eslint-disable-next-line statura/no-floating-point-money -- automatic top-up target is immediately round2'd before persistence, matching award-engine cent rounding.
      const targetPay = round2(breakdown.baseHourlyRate * breakdown.hoursWorked * 2);
      const amount = Math.max(0, targetPay - workedPayForRestTarget(breakdown));
      extraAllowancePay += appendTopUp(extraLines, {
        code: 'REST_BREAK_TOP_UP_ALLOWANCE',
        clauseRef: clauseRefs.join(' / '),
        description: `Automatic rest-break top-up to 200% (${clauseRefs.join(' / ')})`,
        amount,
        units: round2(breakdown.hoursWorked),
        perShift: true,
        componentCode: 'AGED_CARE_REST_BREAK_TOP_UP',
        xeroEarningsCategory: 'AGED_CARE_OT_20',
      });
      if (amount > 0) {
        extraWarnings.push(`Automatic rest-break top-up applied (${clauseRefs.join(' / ')}).`);
      }
    }
  }

  if (awardCode === 'MA000100') {
    const topUpHours = positiveNumber(input.schadsSleepoverFlankingTopUpHours);
    if (topUpHours > 0) {
      extraAllowancePay += appendTopUp(extraLines, {
        code: 'SLEEPOVER_FLANKING_MINIMUM_TOP_UP_ALLOWANCE',
        clauseRef: 'MA000100 cl 25.7(f)',
        description: 'Automatic sleepover flanking-shift top-up to 4 hours',
        // eslint-disable-next-line statura/no-floating-point-money -- top-up is immediately rounded in appendTopUp before persistence/export.
        amount: topUpHours * breakdown.baseHourlyRate,
        units: round2(topUpHours),
        perShift: true,
        componentCode: 'SCHADS_SLEEPOVER_FLANKING_MINIMUM_TOP_UP',
        xeroEarningsCategory: 'SCHADS_ORDINARY',
      });
      extraWarnings.push('Automatic SCHADS sleepover flanking minimum top-up applied.');
    }
  }

  const shouldApplyMinimumEngagementTopUp =
    input.suppressMinimumEngagementTopUp !== true && input.isRemoteWork !== true;

  const hoursForMinimumEngagement =
    positiveNumber(input.minimumEngagementHoursWorkedOverride) || breakdown.hoursWorked;

  if (shouldApplyMinimumEngagementTopUp && hoursForMinimumEngagement > 0) {
    const floor = resolveAwardMinimumEngagementFloor({
      awardCode,
      employmentType,
      classificationStream: input.classificationStream,
      isRemoteWork: input.isRemoteWork,
      profileMinutes: input.minimumEngagementMinutes,
      profileClauseRef: input.minimumEngagementClauseRef,
      profileComponentCode: input.minimumEngagementComponentCode,
      hpssMinimumEngagementHours: input.hpssMinimumEngagementHours,
    });
    if (floor.hours > 0 && hoursForMinimumEngagement < floor.hours) {
      const topUpHours = floor.hours - hoursForMinimumEngagement;
      extraAllowancePay += appendTopUp(extraLines, {
        code: 'MINIMUM_ENGAGEMENT_TOP_UP_ALLOWANCE',
        clauseRef: floor.clauseRef,
        description: `Automatic minimum-engagement top-up to ${floor.hours} hours`,
        amount: topUpHours * workedAverageRate(breakdown),
        units: round2(topUpHours),
        perShift: true,
        componentCode: floor.componentCode,
        xeroEarningsCategory: ordinaryXeroCategoryForAward(awardCode),
      });
      extraWarnings.push('Automatic minimum-engagement top-up applied.');

      // MZ-004 D5: the minimum-engagement hours already make up (part of) the
      // rostered ordinary time lost to the DST-start clock change. Keep only
      // the lost time they do not cover, so no hour is paid twice.
      const reconciled = reconcileDstStartTopUpWithMinimumEngagement(
        engineAllowanceLines,
        topUpHours,
        breakdown.baseHourlyRate,
      );
      if (reconciled.amountDelta !== 0) {
        engineAllowanceLines = reconciled.lines;
        // eslint-disable-next-line statura/no-floating-point-money -- both operands are cent-rounded component amounts; totals are round2'd below.
        extraAllowancePay += reconciled.amountDelta;
        extraWarnings.push(
          'Daylight saving start top-up reduced: the minimum-engagement top-up already pays that time.',
        );
      }
    }
  }

  // appendTopUp only keeps positive lines, so a non-empty list always carries
  // pay; the D5 reconciliation above may lower the net change but never skips it.
  if (extraLines.length === 0) return breakdown;

  const existingTopUps = (breakdown as BreakdownWithTopUps).automaticAwardTopUps?.applied ?? [];
  return {
    ...breakdown,
    // eslint-disable-next-line statura/no-floating-point-money -- both operands are already cent-rounded payroll component amounts; round2 re-normalises before persistence.
    allowancesPay: round2(breakdown.allowancesPay + extraAllowancePay),
    allowanceLines: [...engineAllowanceLines, ...extraLines],

    grossTotal: sumMoney(breakdown.grossTotal, extraAllowancePay),
    warnings: [...breakdown.warnings, ...extraWarnings],
    automaticAwardTopUps: {
      version: AUTOMATIC_AWARD_TOPUP_VERSION,
      applied: [
        ...existingTopUps,
        ...extraLines.map((line) => ({
          code: line.code,
          clauseRef: line.clauseRef ?? '',
          amount: line.amount,
          units: line.units,
        })),
      ],
    },
  } satisfies BreakdownWithTopUps;
}
